import { Flex, SegmentedControl, Text } from '@mantine/core';

interface LabeledSegmentedControlOption<T extends string> {
	label: string;
	value: T;
}

interface Props<T extends string> {
	label?: string;
	value: T;
	onChange: (value: T) => void;
	data: LabeledSegmentedControlOption<T>[];
	disabled?: boolean;
	'data-testid'?: string;
}

// Shared segmented control matching the Figma "Switcher - Toggle"
// (file UlvSXo0qOSeByy4pjrMrCq, node 32293:242887): a grey (#e9ebed) track with a white,
// subtly-shadowed selected pill and 14px/400 Inter text in Dark/8 (#0e171c). Used by the Cap
// "Interest" (TitleRailToggle) and "View" switches and the MIP classifiers filter so all three
// stay visually identical. Values below are the Figma design tokens (colour/greys/*, Dark/8,
// Sans 14/Regular, Elevation/Normal/S) — check a pixel change against that node.
export const LabeledSegmentedControl = <T extends string>({
	label,
	value,
	onChange,
	data,
	disabled,
	'data-testid': dataTestId,
}: Props<T>) => {
	return (
		<Flex align="center" gap="0.5rem">
			{label && (
				<Text fz="sm" c="dark.8">
					{label}
				</Text>
			)}
			<SegmentedControl
				data-testid={dataTestId}
				disabled={disabled}
				// Track fill (colour/greys/2). Set via the `bg` prop, not styles.root — Mantine v7
				// drives the root background through its own --sc-bg, which overrides a styles.root
				// backgroundColor.
				bg="#e9ebed"
				value={value}
				// SegmentedControl emits the raw string value; resolve it back to the typed option
				// (which is always one of `data`) instead of casting.
				onChange={(next) => {
					const match = data.find((option) => option.value === next);
					if (match) {
						onChange(match.value);
					}
				}}
				withItemsBorders={false}
				data={data}
				styles={{
					// Track: greys/4 hairline, 6px radius, 4px inset (fill comes from the bg prop above).
					root: {
						border: '1px solid #d3d8da',
						borderRadius: '6px',
						padding: '4px',
					},
					// Selected pill: white with a greys/0 border and the Elevation/Normal/S drop shadow.
					indicator: {
						backgroundColor: '#ffffff',
						border: '1px solid #f4f5f6',
						borderRadius: '4px',
						boxShadow: '0px 2px 2px #b8bfc2',
					},
					// Sans 14/Regular in Dark/8; 8px×4px padding keeps the pill at the Figma 28px height.
					label: {
						padding: '4px 8px',
						fontSize: '14px',
						fontWeight: 400,
						lineHeight: '20px',
						color: '#0e171c',
					},
					control: {
						border: 'none',
					},
				}}
			/>
		</Flex>
	);
};
